import { classNames } from '@progress/kendo-react-common'
import DataTable from 'components/DataTable'
import Pagination, { IPagination } from 'components/Pagination/Pagination'
import { useAppContext } from 'context/AppContext'
import { useAuth } from 'context/AuthContext'
import { useEffect, useState } from 'react'
import { useFetch } from 'utils/fetch-helper'
import { toHourMinute } from 'utils/string-localization-helper'
import { FetchMethods } from 'utils/types'
import { EventLog, EVENT_ICONS } from '../types'

function EmptyList() {
    return (
        <div className="text-center py-10">
            <div className="rounded-full bg-gray-100 w-20 h-20 inline-block leading-loose text-center align-middle">
                <i className="text-gray-400 feather feather-calendar text-4xl leading-loose" />
            </div>
            <div className="text-gray-400 block mt-4 mx-auto">
                This client currently has no event logs
            </div>
        </div>
    )
}

function RenderList(props: any) {
    const event_logs: EventLog[] = props.event_logs
    const translations: Record<string, string> = props.translations
    const headers = [
        'type',
        'date',
        'time',
        'message'
    ].map(header => {
        return {
            label: translations[header] || header,
            classNames:
                `px-3 py-3 text-left text-xs text-gray-500 uppercase tracking-wider`,
        }
    })
    const rows: HTMLTableRowElement[] = (event_logs || []).map(
        ({ id, icon, date, time, transaction }: EventLog, idx) =>
            (
                <tr
                    key={id}
                    className={classNames(
                        idx % 2 ? 'bg-gray-50' : '',
                        'hover:bg-blue-300 hover:bg-opacity-30'
                    )}
                >
                    <td className="px-3 py-3 align-middle whitespace-nowrap">
                        <i className={classNames(
                            icon.feather_icon,
                            icon.color,
                            "px-2.5 py-1.5 inline-flex text-lg rounded-full"
                        )}/>
                    </td>
                    <td className="px-3 py-3 align-top whitespace-nowrap">
                        <span className="block">
                            {new Date(date).toLocaleString(undefined, {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric',
                            })}
                        </span>
                    </td>
                    <td className="px-3 py-3 align-top whitespace-nowrap">
                        <span className="block">{toHourMinute(new Date(time))}</span>
                    </td>
                    <td className="px-3 py-3 align-top whitespace-nowrap">
                        <span className="block">{transaction}</span>
                    </td>
                </tr>
            ) as unknown as HTMLTableRowElement
    )
    return (
        <div className="flex flex-col">
            <div className="align-middle inline-block min-w-full">
                <div className="overflow-hidden">
                    <DataTable
                        columns={headers}
                        rows={rows}
                    />
                    <Pagination {...props.paginationProps} />
                </div>
            </div>
        </div>
    )
}

export function HistoryLogs() {
    const { translations } = useAppContext()
    const { CreateClientModal } = useAuth()
    const { attributes } = CreateClientModal

    const [event_logs, setEventLogs] = useState<EventLog[]>([])
    const [pageSize, setPageSize] = useState(10)
    const [currentPage, setCurrentPage] = useState(0)
    
     // Events
     const { data } = useFetch(
        `/v1/event/client-id?clientId=${attributes?.id}&size=${pageSize}&page=${currentPage}`,
        FetchMethods.GET,
        !!attributes?.id
    )

    useEffect(() => {
        if (data && data.content) {
            const logs: EventLog[] = data.content.map((log:any) => {
                const icon = EVENT_ICONS[log.eventType] || EVENT_ICONS['OTHER']
                return {
                    id: log.eventId,
                    date: log.createdAt,
                    time: log.createdAt,
                    type: log.eventType,
                    transaction: log.message,
                    icon
                }
            })
            setEventLogs(logs)
        }
    }, [data])
    
    const paginationProps: IPagination = {
        totalPages: data?.totalPages,
        currentPage,
        setCurrentPage,
        pageSize,
        setPageSize,
        totalRecords: data?.totalElements,
    }

    return (
        <div>
            {event_logs.length == 0 ? (
                <EmptyList />
            ) : (
                <RenderList
                    event_logs={event_logs}
                    translations={translations}
                    paginationProps={paginationProps}
                />
            )}
        </div>
    )
}

export default HistoryLogs
